import { Cart, CartItem } from "../../types/cartTypes";
import api from "../../config/Api";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { applyCoupon } from "./couponSlice";
import { createOrder } from "./orderSlice";

interface CartState {
  cart: Cart | null;
  loading: boolean;
  error: string | null;
}

const LOCAL_STORAGE_CART_KEY = "guest_cart_items";

const getGuestCartItemsFromStorage = (): CartItem[] => {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_CART_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveGuestCartItemsToStorage = (items: CartItem[]) => {
  try {
    localStorage.setItem(LOCAL_STORAGE_CART_KEY, JSON.stringify(items));
  } catch (e) {
    console.error("Failed to persist guest cart", e);
  }
};

const calculateStateTotals = (state: CartState) => {
  if (!state.cart || !state.cart.cartItems) return;

  let totalMrp = 0;
  let totalSelling = 0;

  state.cart.cartItems.forEach((item) => {
    const baseSellingPrice = item.product?.sellingPrice || 0;
    const baseMrpPrice = item.product?.mrpPrice || 0;
    const quantity = item.quantity || 1;

    const effectiveSelling = item.sellingPrice !== undefined && item.sellingPrice !== null
      ? item.sellingPrice
      : baseSellingPrice * quantity;

    const effectiveMrp = item.mrpPrice !== undefined && item.mrpPrice !== null
      ? item.mrpPrice
      : baseMrpPrice * quantity;

    totalSelling += effectiveSelling;
    totalMrp += effectiveMrp;
  });

  state.cart.totalMrpPrice = totalMrp;
  state.cart.totalSellingPrice = totalSelling;
};

const initialState: CartState = {
  cart: {
    id: 0,
    user: null as any, // Resolved error #1
    cartItems: getGuestCartItemsFromStorage(),
    totalSellingPrice: 0,
    totalItem: 0,
    totalMrpPrice: 0,
    discount: 0,
    couponCode: null,
  },
  loading: false,
  error: null,
};

const API_URL = "/api/cart";

export const fetchUserCart = createAsyncThunk<Cart, string | null>(
  "cart/fetchUserCart",
  async (jwt: string | null, { rejectWithValue }) => {
    if (!jwt) {
      const guestItems = getGuestCartItemsFromStorage();
      return {
        id: 0,
        user: null as any,
        cartItems: guestItems,
        totalSellingPrice: 0,
        totalItem: guestItems.length,
        totalMrpPrice: 0,
        discount: 0,
        couponCode: null,
      };
    }
    try {
      const response = await api.get(API_URL, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue("Failed to fetch user cart");
    }
  }
);

interface AddItemRequest {
  productId: number | undefined;
  size: string;
  quantity: number;
  product?: any;
}

export const addItemToCart = createAsyncThunk<
  CartItem,
  { jwt: string | null; request: AddItemRequest }
>("cart/addItemToCart", async ({ jwt, request }, { rejectWithValue }) => {
  if (!jwt) {
    const newItem: CartItem = {
      id: Date.now(),
      product: request.product || { id: request.productId },
      size: request.size,
      quantity: request.quantity,
      mrpPrice: (request.product?.mrpPrice || 0) * request.quantity,
      sellingPrice: (request.product?.sellingPrice || 0) * request.quantity,
      userId: 0,
    };
    return newItem;
  }

  try {
    const response = await api.put(`${API_URL}/add`, request, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue("Failed to add item to cart");
  }
});

export const deleteCartItem = createAsyncThunk<
  any,
  { jwt: string | null; cartItemId: number }
>("cart/deleteCartItem", async ({ jwt, cartItemId }, { rejectWithValue }) => {
  if (!jwt) {
    return { cartItemId };
  }
  try {
    const response = await api.delete(`${API_URL}/item/${cartItemId}`, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to delete cart item"
    );
  }
});

export const updateCartItem = createAsyncThunk<
  any,
  { jwt: string | null; cartItemId: number; cartItem: any }
>("cart/updateCartItem", async ({ jwt, cartItemId, cartItem }, { rejectWithValue }) => {
  if (!jwt) {
    return { cartItemId, ...cartItem };
  }
  try {
    const response = await api.put(`${API_URL}/item/${cartItemId}`, cartItem, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to update cart item"
    );
  }
});

export const mergeGuestCart = createAsyncThunk<Cart, string>(
  "cart/mergeGuestCart",
  async (jwt, { rejectWithValue }) => {
    const guestItems = getGuestCartItemsFromStorage();
    if (guestItems.length === 0) {
      // If guest cart is empty, just fetch existing user cart
      const response = await api.get("/api/cart", {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      return response.data;
    }

    try {
      const response = await api.post("/api/cart/merge", guestItems, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      // Clear local storage guest cart after successful sync
      localStorage.removeItem(LOCAL_STORAGE_CART_KEY);
      return response.data;
    } catch (error: any) {
      return rejectWithValue("Failed to merge guest cart");
    }
  }
);

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    resetCartState: (state) => {
      state.cart = null;
      state.loading = false;
      state.error = null;
      localStorage.removeItem(LOCAL_STORAGE_CART_KEY);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserCart.fulfilled, (state, action: PayloadAction<Cart>) => {
        state.cart = action.payload;
        calculateStateTotals(state);
        state.loading = false;
      })
      .addCase(fetchUserCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(addItemToCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addItemToCart.fulfilled, (state, action: PayloadAction<CartItem>) => {
        if (!state.cart) {
          state.cart = {
            id: 0,
            user: null as any, // Resolved error #2
            cartItems: [],
            totalSellingPrice: 0,
            totalItem: 0,
            totalMrpPrice: 0,
            discount: 0,
            couponCode: null,
          };
        }

        // Safe null guard for state.cart (Resolved error #3)
        const cartItems = state.cart.cartItems || [];

        // Check product.id safely (Resolved error #4)
        const existingIndex = cartItems.findIndex(
          (item) =>
            (item.id === action.payload.id ||
              item.product?.id === action.payload.product?.id) &&
            item.size === action.payload.size
        );

        if (existingIndex > -1) {
          cartItems[existingIndex].quantity += action.payload.quantity;
        } else {
          cartItems.push(action.payload);
        }

        state.cart.cartItems = cartItems;
        calculateStateTotals(state);

        if (!localStorage.getItem("jwt")) {
          saveGuestCartItemsToStorage(state.cart.cartItems);
        }

        state.loading = false;
      })
      .addCase(addItemToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteCartItem.fulfilled, (state, action) => {
        if (state.cart) {
          state.cart.cartItems = state.cart.cartItems.filter(
            (item: CartItem) => item.id !== action.meta.arg.cartItemId
          );
          calculateStateTotals(state);
          if (!localStorage.getItem("jwt")) {
            saveGuestCartItemsToStorage(state.cart.cartItems);
          }
        }
        state.loading = false;
      })
      .addCase(updateCartItem.fulfilled, (state, action) => {
        if (state.cart) {
          const index = state.cart.cartItems.findIndex(
            (item: CartItem) => item.id === action.meta.arg.cartItemId
          );
          if (index !== -1) {
            state.cart.cartItems[index] = {
              ...state.cart.cartItems[index],
              ...action.payload,
            };
          }
          calculateStateTotals(state);
          if (!localStorage.getItem("jwt")) {
            saveGuestCartItemsToStorage(state.cart.cartItems);
          }
        }
        state.loading = false;
      })
      .addCase(applyCoupon.fulfilled, (state, action) => {
        state.loading = false;
        state.cart = action.payload;
      })
      .addCase(createOrder.fulfilled, (state) => {
        state.cart = null;
        state.loading = false;
        state.error = null;
        localStorage.removeItem(LOCAL_STORAGE_CART_KEY);
      });
  },
});

export default cartSlice.reducer;
export const { resetCartState } = cartSlice.actions;